/**
 * User Info utility — fetches user details (name, business title) from the
 * Permission Service's /user/{login} endpoint.
 *
 * Auth: Midway (via @amzn/sentry-fetch).
 */

import sentryFetch from '@amzn/sentry-fetch';

import { appConfig } from '../../config';

const PERMISSION_ENDPOINT: Record<string, string> = {
  prod: 'https://prod.permission.dci.aws.a2z.org.cn',
  gamma: 'https://beta.permission.dci.aws.a2z.org.cn',
  beta: 'https://beta.permission.dci.aws.a2z.org.cn',
  dev: 'https://beta.permission.dci.aws.a2z.org.cn',
};

const API_BASE = PERMISSION_ENDPOINT[appConfig.stage] ?? PERMISSION_ENDPOINT.beta;

export interface UserInfoData {
  login: string;
  firstName: string;
  lastName: string;
  businessTitle: string;
}

const cache = new Map<string, UserInfoData>();

export async function getUserInfo(login: string): Promise<UserInfoData | null> {
  if (cache.has(login)) return cache.get(login)!;
  try {
    const resp = await sentryFetch(`${API_BASE}/user/${encodeURIComponent(login)}`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    const user = data.user ?? data;
    const info: UserInfoData = {
      login: user.login ?? login,
      firstName: user.first_name ?? '',
      lastName: user.last_name ?? '',
      businessTitle: user.business_title ?? '',
    };
    cache.set(login, info);
    return info;
  } catch {
    return null;
  }
}
